/**
 * Pure request builders + response unwrappers for the Users & Roles slice.
 *
 * Extracted from `users.ts` so the createApi definition stays under the file
 * budget. These are pure (no RTK dependency), which is exactly why they're
 * exported and wire-shape-locked with `toEqual` in `users.test.ts` /
 * `lib/inviteRules.test.ts` — `fetchBaseQuery` can't be intercepted, so the
 * builder output is how we prove the request matches the live capture.
 *
 * Live wire (captured from portal.dev DevTools, rank-1 — see
 * playwright-temp/users-roles-p1-wire-shapes.md):
 *
 *   GET    /user/v1/search?page&pageSize[&search]&status&sortBy&desc[&merchantId|&vendorId]
 *   GET    /user/v1/invitation/merchant/{merchantId}?…   | /user/v1/vendor-invitation/vendor/{vendorId}?…
 *   PATCH  /user/v1/{userId}   body { enabled }           DELETE /user/v1/{userId}
 *   POST   /user/v1/{userId}/sendPasswordResetLink   (no body; signed-in, no captcha)
 *   POST   /user/v1/invitation/{id}/resend | /user/v1/vendor-invitation/{id}/resend
 *   DELETE /user/v1/invitation/{id}        | /user/v1/vendor-invitation/{id}
 *
 * `desc` and `sortBy` are ALWAYS sent (live shows `desc=false` even at the
 * default) — live (rank-1) overrides the legacy service's `if (desc)` guard.
 */
import { services } from '@config/services';
import type {
  GetUsersArgs,
  UsersSearchResponse,
  User,
  GetMerchantInvitationsArgs,
  GetVendorInvitationsArgs,
  InvitationsResponse,
  Invitation,
  UpdateUserStatusArgs,
  ResendInvitationArgs,
  RevokeInvitationArgs,
  ForgotPasswordArgs,
  UpdateUserArgs,
  UpdateUserAttributesArgs,
  UserRoleArgs,
  GetRolesWithPermissionsArgs,
  SearchRolesArgs,
  GetRoleByIdArgs,
  GetResourcesArgs,
  CreateRoleArgs,
  UpdateRolePermissionsArgs,
  UpdateRoleNameArgs,
  BulkInviteArgs,
  BulkInviteResponse,
  BulkInviteResultRow,
} from './types';

/** The config schema name backing the NotificationsCard. */
export const USERS_CONFIG_SCHEMA = 'UsersConfiguration';

/** Append `&key=enc(value)` only for non-empty values (legacy addParam). */
function appendParam(qs: string, key: string, value: string | undefined | null): string {
  if (value === null || value === undefined || value === '') return qs;
  return `${qs}&${key}=${encodeURIComponent(value)}`;
}

/**
 * Pure request builders — exported so the wire-shape lock test can assert the
 * produced `{ url, method, body }` with `toEqual` (no extra/missing params).
 */
export const usersRequestBuilders = {
  getUsers: ({ page, pageSize, sortBy, desc, status, search, merchantId, vendorId }: GetUsersArgs) => {
    let qs = `page=${page}&pageSize=${pageSize}`;
    qs = appendParam(qs, 'search', search);
    qs = appendParam(qs, 'status', status);
    qs += `&sortBy=${encodeURIComponent(sortBy)}`;
    qs += `&desc=${desc}`;
    if (merchantId) qs += `&merchantId=${encodeURIComponent(merchantId)}`;
    else if (vendorId) qs += `&vendorId=${encodeURIComponent(vendorId)}`;
    return { url: `${services.user}/v1/search?${qs}`, method: 'GET' as const };
  },

  getMerchantInvitations: ({
    merchantId,
    page,
    pageSize,
    status,
    dateFrom,
    dateTo,
    deliveryMethod = 'Email',
    sortBy,
    desc,
  }: GetMerchantInvitationsArgs) => {
    let qs = `page=${page}&pageSize=${pageSize}`;
    qs = appendParam(qs, 'status', status);
    qs = appendParam(qs, 'dateFrom', dateFrom);
    qs = appendParam(qs, 'dateTo', dateTo);
    qs = appendParam(qs, 'deliveryMethod', deliveryMethod);
    qs = appendParam(qs, 'sortBy', sortBy);
    qs = appendParam(qs, 'desc', String(desc));
    return {
      url: `${services.user}/v1/invitation/merchant/${encodeURIComponent(merchantId)}?${qs}`,
      method: 'GET' as const,
    };
  },

  getVendorInvitations: ({
    vendorId,
    page,
    pageSize,
    status,
    dateFrom,
    dateTo,
    deliveryMethod = 'Email',
    sortBy,
    desc,
  }: GetVendorInvitationsArgs) => {
    let qs = `page=${page}&pageSize=${pageSize}`;
    qs = appendParam(qs, 'status', status);
    qs = appendParam(qs, 'dateFrom', dateFrom);
    qs = appendParam(qs, 'dateTo', dateTo);
    qs = appendParam(qs, 'deliveryMethod', deliveryMethod);
    qs = appendParam(qs, 'sortBy', sortBy);
    qs = appendParam(qs, 'desc', String(desc));
    return {
      url: `${services.user}/v1/vendor-invitation/vendor/${encodeURIComponent(vendorId)}?${qs}`,
      method: 'GET' as const,
    };
  },

  updateUserStatus: ({ userId, enabled }: UpdateUserStatusArgs) => ({
    url: `${services.user}/v1/${encodeURIComponent(userId)}`,
    method: 'PATCH' as const,
    body: { enabled },
  }),

  deleteUser: ({ userId }: { userId: string }) => ({
    url: `${services.user}/v1/${encodeURIComponent(userId)}`,
    method: 'DELETE' as const,
  }),

  /**
   * Signed-in reset-link request. The anonymous `/v1/forgot-password` is
   * captcha-protected and reserved for the public forgot-password page; this
   * route is scoped by the caller's token instead (self, admin, or users-update
   * for a user of the caller's own or associated organization), so it needs
   * no captcha and no body.
   */
  requestForgotPassword: ({ userId }: ForgotPasswordArgs) => ({
    url: `${services.user}/v1/${encodeURIComponent(userId)}/sendPasswordResetLink`,
    method: 'POST' as const,
  }),

  resendInvitation: ({ invitationId, entityType }: ResendInvitationArgs) => ({
    url:
      entityType === 'vendor'
        ? `${services.user}/v1/vendor-invitation/${encodeURIComponent(invitationId)}/resend`
        : `${services.user}/v1/invitation/${encodeURIComponent(invitationId)}/resend`,
    method: 'POST' as const,
  }),

  revokeInvitation: ({ invitationId, entityType }: RevokeInvitationArgs) => ({
    url:
      entityType === 'vendor'
        ? `${services.user}/v1/vendor-invitation/${encodeURIComponent(invitationId)}`
        : `${services.user}/v1/invitation/${encodeURIComponent(invitationId)}`,
    method: 'DELETE' as const,
  }),

  // ─── Phase 2 (User Detail) ─────────────────────────────────────────────
  getUserById: ({ userId }: { userId: string }) => ({
    url: `${services.user}/v1/${encodeURIComponent(userId)}`,
    method: 'GET' as const,
  }),

  /** UsersConfiguration values for a user (NotificationsCard). */
  getUserConfiguration: ({ userId }: { userId: string }) => ({
    url: `${services.config}/v1/config?ownerId=${encodeURIComponent(userId)}&schemaName=${USERS_CONFIG_SCHEMA}`,
    method: 'GET' as const,
  }),

  /** UsersConfiguration JSON schema (NotificationsCard labels + frequency enum). */
  getUserSchema: () => ({
    url: `${services.config}/v1/schema/${USERS_CONFIG_SCHEMA}`,
    method: 'GET' as const,
  }),

  // ─── Phase 2b (edit mode) ──────────────────────────────────────────────
  /**
   * Role-dropdown options for the edit form. LIVE-confirmed
   * (portal.dev DevTools, 2026-06-01): `GET /user/v1/roles/permissions
   * [?merchantId={entityId}]`. `merchantId` is appended only when the user is
   * merchant-scoped (legacy `getRoleWithPermissions(merchantId)`).
   */
  getRolesWithPermissions: ({ merchantId }: GetRolesWithPermissionsArgs) => {
    let url = `${services.user}/v1/roles/permissions`;
    if (merchantId) url += `?merchantId=${encodeURIComponent(merchantId)}`;
    return { url, method: 'GET' as const };
  },

  /**
   * PATCH the user's profile + notification config in one call. Body is the
   * legacy `UserServices.updateUser` `data` verbatim. NOTE: locked from the
   * rank-3 service layer (firing a real PATCH would mutate a shared dev user).
   */
  updateUser: ({
    userId,
    firstName,
    lastName,
    email,
    EmailSettingsEnabled,
    EmailSettingsFrequency,
    UserSchemaVersion,
  }: UpdateUserArgs) => ({
    url: `${services.user}/v1/${encodeURIComponent(userId)}`,
    method: 'PATCH' as const,
    body: { firstName, lastName, email, EmailSettingsEnabled, EmailSettingsFrequency, UserSchemaVersion },
  }),

  /** PUT the user's org attributes (note wire spelling `associatedIDs`). */
  updateUserAttributes: ({ userId, type, entity, entityId, associatedIDs }: UpdateUserAttributesArgs) => ({
    url: `${services.user}/v1/${encodeURIComponent(userId)}/attributes`,
    method: 'PUT' as const,
    body: { type, entity, entityId, associatedIDs },
  }),

  /**
   * Role-search backing the Switch-Org role dropdown. LIVE-confirmed
   * (2026-06-01): `GET /user/v1/roles/search?page&pageSize[&search]&sortBy
   * &desc[&merchantId|&vendorId]`. `sortBy`+`desc` ALWAYS sent.
   */
  searchRoles: ({ page, pageSize, sortBy, desc, search, merchantId, vendorId }: SearchRolesArgs) => {
    let qs = `page=${page}&pageSize=${pageSize}`;
    qs = appendParam(qs, 'search', search);
    qs += `&sortBy=${encodeURIComponent(sortBy)}`;
    qs += `&desc=${desc}`;
    if (merchantId) qs += `&merchantId=${encodeURIComponent(merchantId)}`;
    else if (vendorId) qs += `&vendorId=${encodeURIComponent(vendorId)}`;
    return { url: `${services.user}/v1/roles/search?${qs}`, method: 'GET' as const };
  },

  /** POST assign a role to the user (`{ roleId }`). */
  assignRole: ({ userId, roleId }: UserRoleArgs) => ({
    url: `${services.user}/v1/${encodeURIComponent(userId)}/role`,
    method: 'POST' as const,
    body: { roleId },
  }),

  /** DELETE unassign a role from the user (body `{ roleId }`). */
  unassignRole: ({ userId, roleId }: UserRoleArgs) => ({
    url: `${services.user}/v1/${encodeURIComponent(userId)}/role`,
    method: 'DELETE' as const,
    body: { roleId },
  }),

  // ─── Phase 3 (Roles) ───────────────────────────────────────────────────
  /** DELETE a role (Roles list, only enabled when usersCount === 0). */
  deleteRole: ({ roleId }: { roleId: string }) => ({
    url: `${services.user}/v1/roles/${encodeURIComponent(roleId)}`,
    method: 'DELETE' as const,
  }),

  /**
   * GET a role's full permission set. LIVE-confirmed (2026-06-01):
   * `/user/v1/roles/{id}/permissions?filterByUserAccess=true
   * [&merchantId|vendorId]`.
   */
  getRoleById: ({ roleId, merchantId, vendorId }: GetRoleByIdArgs) => {
    let url = `${services.user}/v1/roles/${encodeURIComponent(roleId)}/permissions?filterByUserAccess=true`;
    if (merchantId) url += `&merchantId=${encodeURIComponent(merchantId)}`;
    else if (vendorId) url += `&vendorId=${encodeURIComponent(vendorId)}`;
    return { url, method: 'GET' as const };
  },

  /**
   * GET the assignable resources for create-role. LIVE-confirmed:
   * `/user/v1/resources?organizationType={merchant|vendor}
   * [&merchantId|vendorId]`.
   */
  getResources: ({ organizationType, merchantId, vendorId }: GetResourcesArgs) => {
    const params: string[] = [];
    if (organizationType) params.push(`organizationType=${encodeURIComponent(organizationType)}`);
    if (merchantId) params.push(`merchantId=${encodeURIComponent(merchantId)}`);
    else if (vendorId) params.push(`vendorId=${encodeURIComponent(vendorId)}`);
    const url = `${services.user}/v1/resources${params.length ? `?${params.join('&')}` : ''}`;
    return { url, method: 'GET' as const };
  },

  /**
   * POST a new role. The server derives the role's OwnerId from the
   * merchant/vendor scope in the REQUEST BODY — NOT a query param. Verified
   * rank-1 on portal.dev (legacy admin → invite → create role): the working
   * request is `POST /user/v1/roles` with a clean URL and body
   * `{ name, merchantId }` (or `{ name, vendorId }`).
   * Sending the scope as `?merchantId=` (the previous, rank-3 guess) is ignored
   * by the server → 400 "Role OwnerId could not be determined from the request".
   * (Contrast: the permissions PUT below DOES scope via the `?merchantId=` query.)
   */
  createRole: ({ name, merchantId, vendorId }: CreateRoleArgs) => {
    const scope: { merchantId?: string; vendorId?: string } = {};
    if (merchantId) scope.merchantId = merchantId;
    else if (vendorId) scope.vendorId = vendorId;
    return {
      url: `${services.user}/v1/roles`,
      method: 'POST' as const,
      body: { name, ...scope },
    };
  },

  /**
   * PUT a role's permissions. Body = `[{ resourceId, resourceName,
   * permissions: [name] }]`.
   */
  updateRolePermissions: ({ roleId, permissions }: UpdateRolePermissionsArgs) => ({
    url: `${services.user}/v1/roles/${encodeURIComponent(roleId)}/permissions`,
    method: 'PUT' as const,
    body: permissions,
  }),

  /** PUT a role's name (legacy `updateRoleName`): `{ name }`. */
  updateRoleName: ({ roleId, name }: UpdateRoleNameArgs) => ({
    url: `${services.user}/v1/roles/${encodeURIComponent(roleId)}`,
    method: 'PUT' as const,
    body: { name },
  }),

  // ─── Phase 4 (Invite User wizard) ──────────────────────────────────────
  /**
   * POST validate a batch of invite emails (merchant path). LIVE-confirmed:
   * body is a **bare `string[]`** (not an object), response
   * `{ existingUsers, invalidEmails, pendingInvitations, expiredInvitations }`.
   */
  validateInviteEmails: (emails: string[]) => ({
    url: `${services.user}/v1/invitation/validateEmails`,
    method: 'POST' as const,
    body: emails,
  }),

  /** POST validate a batch of invite emails (vendor path); same bare-array body. */
  validateVendorInvitationEmails: (emails: string[]) => ({
    url: `${services.user}/v1/vendor-invitation/validateEmails`,
    method: 'POST' as const,
    body: emails,
  }),

  /**
   * POST the merchant bulk invite. Body `{ bulk: [Entry] }` (saga
   * `handleSubmitInvite`). NOTE: rank-3 — never fired live; entry composition
   * is locked in `lib/inviteRules` with `toEqual`.
   */
  bulkInviteSubaccount: ({ bulk }: BulkInviteArgs) => ({
    url: `${services.user}/v1/invitation/bulk`,
    method: 'POST' as const,
    body: { bulk },
  }),

  /** POST the vendor bulk invite (`/vendor-invitation/bulk`); same `{ bulk }` body. */
  bulkInviteVendor: ({ bulk }: BulkInviteArgs) => ({
    url: `${services.user}/v1/vendor-invitation/bulk`,
    method: 'POST' as const,
    body: { bulk },
  }),
};

/** Unwrap the bulk-invite response (`{ bulk: [...] }` | bare array). */
export function unwrapBulkInvite(response: BulkInviteResponse): BulkInviteResultRow[] {
  if (Array.isArray(response)) return response;
  return response.bulk ?? [];
}

/** Unwrap `{ users }` (tolerates a bare array, defends a missing key). */
export function unwrapUsers(response: UsersSearchResponse | User[]): UsersSearchResponse {
  if (Array.isArray(response)) {
    return { page: 1, pageSize: response.length, total: response.length, users: response };
  }
  return {
    page: response.page ?? 1,
    pageSize: response.pageSize ?? 0,
    total: response.total ?? response.users?.length ?? 0,
    users: response.users ?? [],
  };
}

/** Unwrap `{ invitations, total }` (tolerates a bare array). */
export function unwrapInvitations(response: InvitationsResponse | Invitation[]): InvitationsResponse {
  if (Array.isArray(response)) {
    return { invitations: response, total: response.length };
  }
  return { invitations: response.invitations ?? [], total: response.total ?? 0 };
}
